import React, { useEffect, useState } from 'react';
import { useWebSocketContext, Notification } from '@/context/WebSocketContext';
import { IoAlertCircleSharp, IoInformationCircle, IoWarning, IoCheckmarkCircle, IoClose } from "react-icons/io5";

export default function CustomNotificationDisplay() {
  const { notifications, clearNotification } = useWebSocketContext();
  const [visibleNotifications, setVisibleNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    const importantNotifications = notifications.filter(
      n => n.severity === 'error' || n.severity === 'warning' || 
           n.severity === 'info' || n.actionRequired
    );
    
    setVisibleNotifications(importantNotifications);
  }, [notifications]);

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <IoAlertCircleSharp className="h-6 w-6" />;
      case 'warning':
        return <IoWarning className="h-6 w-6" />;
      case 'success':
        return <IoCheckmarkCircle className="h-6 w-6" />;
      case 'info':
      default:
        return <IoInformationCircle className="h-6 w-6" />;
    }
  };

  const getSeverityClasses = (severity: string) => {
    switch (severity) {
      case 'error':
        return "border-danger-600 bg-gradient-to-b from-red-700 to-red-500 text-white dark:bg-danger-700 dark:text-white";
      case 'warning':
        return "border-warning-600 bg-gradient-to-b from-warning-500 to-warning-400  dark:bg-warning-800 dark:text-warning-700";
      case 'success':
        return "border-success-500 bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-300";
      case 'info':
      default:
        return "border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300";
    }
  };

  const handleClose = (id: string) => {
    clearNotification(id);
  };

  if (visibleNotifications.length === 0) {
    return null;
  }

  return (
    <div className="flex -mt-10 relative z-[99]">
      {visibleNotifications.map((notification) => (
        <div 
          key={notification.id}
          className={`w-full relative flex items-start border p-4 rounded-md shadow-md ${getSeverityClasses(notification.severity)}`}
        >
          <div className="mr-3 mt-0.5">
            {getSeverityIcon(notification.severity)}
          </div>
          
          <div className="flex-1">
            <h4 className="font-semibold mb-1">{notification.type}</h4>
            <p className="text-sm opacity-90">{notification.message}</p>
            
            {notification.actionRequired && (
              <div className="mt-3 flex gap-2">
                {notification.actionLink && (
                  <a 
                    href={notification.actionLink}
                    className={`px-3 py-1 text-sm font-medium rounded-md border 
                      ${notification.severity === 'error' ? 'border-white bg-danger-700 text-white' :
                       notification.severity === 'warning' ? 'border-warning-600 bg-warning-50 text-warning-800' : 
                       'border-primary-500 text-primary-700'}`}
                  >
                    Voir détails
                  </a>
                )}
                {(notification.severity !== 'error' || notification.actionRequired) && (
                  <button 
                    onClick={() => handleClose(notification.id)}
                    className={`px-3 py-1 text-sm underline-offset-2 hover:opacity-90 
                      ${notification.severity === 'error' ? 'text-white' : 
                       notification.severity === 'warning' ? 'text-warning-800' : ''}`}
                  >
                    Ignorer
                  </button>
                )}
              </div>
            )}
          </div>
          
          {notification.severity !== 'error' && (
            <button 
              onClick={() => handleClose(notification.id)} 
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
              aria-label="Fermer"
            >
              <IoClose className="h-4 w-4 opacity-80" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}